import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import {
  MdKeyboardArrowDown,
  MdEdit,
  MdDeleteOutline,
  MdOutlineCheckCircle,
  MdOutlineRemoveCircleOutline,
  MdOutlineEmail,
  MdSecurity,
  MdAddCircle,
  MdFileDownload,
} from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Utils,
  fetchWithAuth,
  selectStyles,
  handleSessionExpired,
  getDefaultPageSize,
  DEFAULT_STATUS_OPTIONS,
  formatDateTime,
  TEAM_MEMBER_ROLE_SLUG,
  getStaffStatusDisplay,
  can,
  NsPersonCell,
  exportListCsv,
} from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

const emptyForm = { id: null, name: "", email: "", role_id: null };

function isStaffPending(user) {
  if (user?.is_member_active) return false;
  if (user?.is_support_team_member) {
    return Number(user?.membership_status) === 1;
  }
  return Number(user?.status) === 1;
}

const Staff = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ name: "", email: "", role_id: "", status: "" });
  const [draftFilters, setDraftFilters] = useState({ name: "", email: "", role_id: "", status: "" });
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsStaff, setPermissionsStaff] = useState(null);
  const [permissionSections, setPermissionSections] = useState([]);
  /** { [permission_key]: number[] resource_ids } */
  const [selectedByPermissionKey, setSelectedByPermissionKey] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [openPermissionSectionKey, setOpenPermissionSectionKey] = useState("campaign_link");
  const [openPermissionAppKeys, setOpenPermissionAppKeys] = useState({});

  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const {
    openActionId,
    openActionItem,
    dropdownPosition,
    dropdownRef,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({
    tableScrollRef,
    optionCount: () =>
      (can("team.edit") ? 3 : 0) +
      (can("team.change_status") ? 1 : 0) +
      (can("team.delete") ? 1 : 0),
  });

  const selectedRole = useMemo(
    () => roleOptions.find((o) => Number(o.value) === Number(form.role_id)) || null,
    [roleOptions, form.role_id]
  );
  const isSupportTeamRole = selectedRole?.slug === TEAM_MEMBER_ROLE_SLUG;

  const loadRoles = async () => {
    const res = await fetchWithAuth(`${Utils.baseurl}user/roles`, {
      method: "POST",
      body: JSON.stringify({ page: 1, pageSize: 100, status: 1 }),
    });
    const result = await res.json().catch(() => ({}));
    if (handleSessionExpired(res, result)) return;
    if (result.status) {
      setRoleOptions(
        (result.data || []).map((r) => ({
          value: r.id,
          label: r.name,
          slug: r.slug || "",
        }))
      );
    }
  };

  const loadData = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    const res = await fetchWithAuth(`${Utils.baseurl}user/staff`, {
      method: "POST",
      body: JSON.stringify({
        page: nextPage,
        pageSize,
        name: (nextFilters.name || "").trim(),
        email: (nextFilters.email || "").trim(),
        role_id: nextFilters.role_id,
        status: nextFilters.status,
      }),
    });
    const result = await res.json().catch(() => ({}));
    setLoading(false);
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Failed to load staff");
    setRows(result.data || []);
    setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
  };

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const name = searchParams.get("name") || "";
    const email = searchParams.get("email") || "";
    const role_id = searchParams.get("role_id") || "";
    const status = searchParams.get("status") || "";
    const f = { name, email, role_id, status };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(name || email || role_id || status));
    loadData(p, f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.name || "").trim()) sp.set("name", f.name.trim());
    if ((f.email || "").trim()) sp.set("email", f.email.trim());
    if (String(f.role_id || "").trim() !== "") sp.set("role_id", String(f.role_id).trim());
    if (String(f.status || "").trim() !== "") sp.set("status", String(f.status).trim());
    setSearchParams(sp, { replace: true });
  };

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    writeParams(1, f);
  };

  const onResetFilters = () => {
    const f = { name: "", email: "", role_id: "", status: "" };
    setDraftFilters(f);
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, f);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setFormErrors({});
    loadRoles();
    setShowModal(true);
  };

  const openEdit = (user) => {
    setForm({
      id: user.id,
      name: user.name || "",
      email: user.email || "",
      role_id: user.role_id || null,
    });
    setFormErrors({});
    loadRoles();
    setShowModal(true);
  };

  const changeStatus = async (user, nextStatus) => {
    if (nextStatus === 1 && !user.is_member_active) {
      toast.info("Member has not activated yet. Send activation email instead.");
      return;
    }
    const res = await fetchWithAuth(`${Utils.baseurl}user/staff/status`, {
      method: "POST",
      body: JSON.stringify({
        id: user.id,
        status: nextStatus,
      }),
    });
    const result = await res.json().catch(() => ({}));
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Status update failed");
    toast.success(result.message || "Status updated");
    loadData(page, filters);
  };

  const sendActivation = async (user) => {
    const confirm = await Swal.fire({
      title: "Send activation email?",
      text: "An activation email will be sent so they can set their password.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, send",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}user/staff/resend-activation`, {
      method: "POST",
      body: JSON.stringify({ id: user.id, origin: window.location.origin }),
    });
    const result = await res.json().catch(() => ({}));
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Failed to send activation email");
    toast.success(result.message || "Activation email sent");
    loadData(page, filters);
  };

  const save = async () => {
    const errors = {};
    if (!(form.name || "").trim()) errors.name = "Name is required";
    if (!(form.email || "").trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = "Valid email is required";
    if (!form.role_id) errors.role_id = "Role is required";

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    try {
      const body = {
        id: form.id || undefined,
        name: form.name.trim(),
        email: form.email.trim(),
        role_id: form.role_id,
        origin: window.location.origin,
      };
      const res = await fetchWithAuth(`${Utils.baseurl}user/staff/save`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const result = await res.json().catch(() => ({}));
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        toast.error(result.message || "Save failed");
        return;
      }
      toast.success(result.message || "Saved");
      setShowModal(false);
      loadRoles();
      loadData(page, filters);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    const confirm = await Swal.fire({
      title: "Delete staff?",
      text: `"${user.name || user.email}" will be permanently deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}user/staff/delete`, {
      method: "POST",
      body: JSON.stringify({ id: user.id }),
    });
    const result = await res.json().catch(() => ({}));
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Delete failed");
    toast.success(result.message || "Deleted");
    loadData(page, filters);
  };

  const openPermissionsModal = async (user) => {
    setPermissionsStaff(user);
    setShowPermissionsModal(true);
    setPermissionsLoading(true);
    setPermissionSections([]);
    setSelectedByPermissionKey({});
    setOpenPermissionSectionKey("campaign_link");
    setOpenPermissionAppKeys({});
    try {
      const res = await fetchWithAuth(`${Utils.baseurl}user/staff/permissions`, {
        method: "POST",
        body: JSON.stringify({ staff_id: user.id }),
      });
      const result = await res.json().catch(() => ({}));
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        toast.error(result.message || "Failed to load user permissions");
        setShowPermissionsModal(false);
        return;
      }
      const sections = result.data?.sections || [];
      setPermissionSections(sections);
      const selectedMap = {};
      const appOpen = {};
      for (const section of sections) {
        const key = section.permission_key;
        selectedMap[key] = [];
        for (const app of section.apps || []) {
          const appKey = `${key}:${app.applicationId}`;
          appOpen[appKey] = true;
          for (const resource of app.resources || []) {
            if (resource.selected) selectedMap[key].push(Number(resource.resource_id));
          }
        }
      }
      setOpenPermissionAppKeys(appOpen);
      setSelectedByPermissionKey(selectedMap);
      if (sections[0]?.permission_key) setOpenPermissionSectionKey(sections[0].permission_key);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const isResourceSelected = (permissionKey, resourceId) =>
    (selectedByPermissionKey[permissionKey] || []).map(Number).includes(Number(resourceId));

  const toggleResourceId = (permissionKey, resourceId) => {
    const n = Number(resourceId);
    setSelectedByPermissionKey((prev) => {
      const set = new Set((prev[permissionKey] || []).map(Number));
      if (set.has(n)) set.delete(n);
      else set.add(n);
      return { ...prev, [permissionKey]: [...set] };
    });
  };

  const toggleSectionResources = (permissionKey, resources) => {
    const ids = (resources || []).map((r) => Number(r.resource_id));
    if (!ids.length) return;
    setSelectedByPermissionKey((prev) => {
      const set = new Set((prev[permissionKey] || []).map(Number));
      const allSelected = ids.every((id) => set.has(id));
      if (allSelected) ids.forEach((id) => set.delete(id));
      else ids.forEach((id) => set.add(id));
      return { ...prev, [permissionKey]: [...set] };
    });
  };

  const selectedPermissionsCount = useMemo(
    () => Object.values(selectedByPermissionKey).reduce((sum, ids) => sum + (ids?.length || 0), 0),
    [selectedByPermissionKey]
  );

  const savePermissions = async () => {
    if (!permissionsStaff?.id) return;
    setPermissionsSaving(true);
    try {
      const permissions = {};
      for (const section of permissionSections) {
        const key = section.permission_key;
        if (!key) continue;
        permissions[key] = selectedByPermissionKey[key] || [];
      }
      const res = await fetchWithAuth(`${Utils.baseurl}user/staff/permissions/save`, {
        method: "POST",
        body: JSON.stringify({
          staff_id: permissionsStaff.id,
          permissions,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        toast.error(result.message || "Failed to save user permissions");
        return;
      }
      toast.success(result.message || "User permissions updated");
      setShowPermissionsModal(false);
    } finally {
      setPermissionsSaving(false);
    }
  };

  const selectedRoleFilter = useMemo(
    () => roleOptions.find((o) => String(o.value) === String(draftFilters.role_id)) || null,
    [roleOptions, draftFilters.role_id]
  );

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("user/staff", {
        name: (filters.name || "").trim(),
        email: (filters.email || "").trim(),
        role_id: filters.role_id,
        status: filters.status,
      }, "staff.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner ns-support-list">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Staff</h1>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              Manage account staff. New staff get an activation email to set their password. Support Team Member invites sync to all Customer Support apps.
            </p>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData()}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("team.add") ? (
              <button type="button" className="add_app_btn btn_theme_primary" onClick={openCreate}>
                <MdAddCircle />
                <span>Add Staff</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((v) => !v)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`} size={22} />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input
                      className="form-control"
                      placeholder="Name"
                      value={draftFilters.name}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input
                      className="form-control"
                      placeholder="Email"
                      value={draftFilters.email}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, email: e.target.value }))}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="staff-filter-role"
                      instanceId="staff-filter-role"
                      classNamePrefix="ns-staff-filter-role"
                      isSearchable
                      isClearable
                      placeholder="All Roles"
                      options={roleOptions}
                      value={selectedRoleFilter}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, role_id: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="staff-filter-status"
                      instanceId="staff-filter-status"
                      classNamePrefix="ns-staff-filter-status"
                      isSearchable
                      isClearable
                      placeholder="All Status"
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => String(o.value) === String(draftFilters.status)) || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, status: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}><span>Search</span></button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}><span>Reset</span></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{ maxHeight: tableMaxHeight || undefined, overflow: "auto" }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Apps</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="7" className="text-center">Loading...</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan="7" className="text-center">No staff found</td></tr>
                    ) : rows.map((user) => {
                      const statusUi = getStaffStatusDisplay({
                        status: user.is_support_team_member ? user.membership_status : user.status,
                        is_member_active: user.is_member_active,
                      });
                      return (
                        <tr key={user.id}>
                          <td><NsPersonCell name={user.name} email={user.email} /></td>
                          <td>{user.email || "—"}</td>
                          <td>{user.role_name || "—"}</td>
                          <td style={{ maxWidth: 180 }}>
                            {user.is_support_team_member
                              ? (user.app_names?.length ? user.app_names.join(", ") : "—")
                              : "—"}
                          </td>
                          <td>
                            <span className={statusUi.badgeClass || "badge bg-light-secondary"}>
                              {statusUi.label}
                            </span>
                          </td>
                          <td>{user.updatedAt ? formatDateTime(user.updatedAt) : "—"}</td>
                          <td className="col-action" style={{ textAlign: "right" }}>
                            <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                              <button
                                type="button"
                                className="dropdown-trigger-btn btn btn-link p-0"
                                aria-label="Actions"
                                aria-expanded={openActionId === user.id}
                                onClick={(e) => handleOpenDropdown(e, user.id, user)}
                              >
                                <BsThreeDotsVertical />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total || 0}
                pageSize={pageSize}
                totalPages={meta.totalPages || 1}
                ariaLabel="Staff pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {showModal ? (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.35)" }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{form.id ? "Edit Staff" : "Create Staff"}</h5>
                  <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={() => { setShowModal(false); setFormErrors({}); }} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label mb-1">Name <span className="text-danger">*</span></label>
                    <input
                      className={`form-control${formErrors.name ? " is-invalid" : ""}`}
                      value={form.name}
                      onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setFormErrors((p) => ({ ...p, name: "" })); }}
                    />
                    {formErrors.name && <div className="invalid-feedback d-block">{formErrors.name}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label mb-1">Email <span className="text-danger">*</span></label>
                    <input
                      className={`form-control${formErrors.email ? " is-invalid" : ""}`}
                      type="email"
                      value={form.email}
                      onChange={(e) => { setForm((p) => ({ ...p, email: e.target.value })); setFormErrors((p) => ({ ...p, email: "" })); }}
                    />
                    {formErrors.email && <div className="invalid-feedback d-block">{formErrors.email}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label mb-1">Role <span className="text-danger">*</span></label>
                    <Select
                      inputId="staff-modal-role"
                      instanceId="staff-modal-role"
                      classNamePrefix="ns-staff-modal-role"
                      isSearchable
                      placeholder="Select role..."
                      options={roleOptions}
                      value={selectedRole}
                      onChange={(opt) => {
                        setForm((p) => ({ ...p, role_id: opt?.value ?? null }));
                        setFormErrors((p) => ({ ...p, role_id: "" }));
                      }}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                    {formErrors.role_id && <div className="invalid-feedback d-block">{formErrors.role_id}</div>}
                    {!form.id && (
                      <div className="form-text mt-1">An activation email will be sent so they can set their password.</div>
                    )}
                    {isSupportTeamRole && (
                      <div className="form-text mt-1">Customer Support access is granted for all apps under this account.</div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="add_app_btn btn_theme_primary" onClick={save} disabled={saving}>
                    <span>{saving ? "Saving..." : "Save"}</span>
                  </button>
                  <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={() => { setShowModal(false); setFormErrors({}); }}>
                    <span>Close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {showPermissionsModal ? (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.35)" }}>
            <div
              className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
              role="document"
              style={{ maxHeight: "90vh" }}
            >
              <div className="modal-content" style={{ maxHeight: "90vh" }}>
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title mb-0">User Permissions</h5>
                    <p className="text-muted mb-0 mt-1" style={{ fontSize: 12 }}>
                      {permissionsStaff?.name || permissionsStaff?.email || "Staff"} — grant resource access beyond what they create.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    aria-label="Close"
                    onClick={() => setShowPermissionsModal(false)}
                  />
                </div>
                <div className="modal-body" style={{ overflowY: "auto", minHeight: 0 }}>
                  {permissionsLoading ? (
                    <div className="text-center py-4 text-muted">Loading...</div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label mb-0">Permissions</label>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          {selectedPermissionsCount} selected
                        </span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {permissionSections.length === 0 ? (
                          <div className="text-muted text-center py-3" style={{ fontSize: 13 }}>
                            No resources available to assign.
                          </div>
                        ) : (
                          permissionSections.map((section) => {
                            const permissionKey = section.permission_key;
                            const sectionOpen = openPermissionSectionKey === permissionKey;
                            const sectionResources = (section.apps || []).flatMap((a) => a.resources || []);
                            const sectionAllSelected =
                              sectionResources.length > 0 &&
                              sectionResources.every((r) => isResourceSelected(permissionKey, r.resource_id));
                            return (
                              <div
                                key={permissionKey}
                                style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff" }}
                              >
                                <button
                                  type="button"
                                  onClick={() => setOpenPermissionSectionKey(sectionOpen ? "" : permissionKey)}
                                  style={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    padding: "12px 14px",
                                    border: "none",
                                    borderRadius: sectionOpen ? "10px 10px 0 0" : 10,
                                    background: sectionOpen ? "#f9fafb" : "#fff",
                                    cursor: "pointer",
                                    textAlign: "left",
                                  }}
                                >
                                  <span style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, color: "#111827" }}>
                                    <input
                                      type="checkbox"
                                      checked={sectionAllSelected}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        toggleSectionResources(permissionKey, sectionResources);
                                      }}
                                    />
                                    {section.label}
                                  </span>
                                  <MdKeyboardArrowDown
                                    size={20}
                                    style={{
                                      color: "#6b7280",
                                      transform: sectionOpen ? "rotate(180deg)" : "rotate(0deg)",
                                      transition: "transform 0.15s ease",
                                      flexShrink: 0,
                                    }}
                                  />
                                </button>
                                <Collapse in={sectionOpen}>
                                  <div>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                        padding: "12px",
                                        maxHeight: 360,
                                        overflowY: "auto",
                                      }}
                                    >
                                      {(section.apps || []).length === 0 ? (
                                        <div className="text-muted py-2" style={{ fontSize: 12 }}>
                                          No resources from other users for this account.
                                        </div>
                                      ) : (
                                        (section.apps || []).map((app) => {
                                          const appKey = `${permissionKey}:${app.applicationId}`;
                                          const appOpen = Boolean(openPermissionAppKeys[appKey]);
                                          const appResources = app.resources || [];
                                          const appAllSelected =
                                            appResources.length > 0 &&
                                            appResources.every((r) => isResourceSelected(permissionKey, r.resource_id));
                                          return (
                                            <div
                                              key={appKey}
                                              style={{ border: "1px solid #e5e7eb", borderRadius: 8 }}
                                            >
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setOpenPermissionAppKeys((prev) => ({
                                                    ...prev,
                                                    [appKey]: !prev[appKey],
                                                  }))
                                                }
                                                style={{
                                                  width: "100%",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "space-between",
                                                  gap: 12,
                                                  padding: "10px 12px",
                                                  border: "none",
                                                  borderRadius: appOpen ? "8px 8px 0 0" : 8,
                                                  background: appOpen ? "#f9fafb" : "#fff",
                                                  cursor: "pointer",
                                                  textAlign: "left",
                                                }}
                                              >
                                                <span style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, fontSize: 13, color: "#111827" }}>
                                                  <input
                                                    type="checkbox"
                                                    checked={appAllSelected}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => {
                                                      e.stopPropagation();
                                                      toggleSectionResources(permissionKey, appResources);
                                                    }}
                                                  />
                                                  {app.application_name || app.app_id}
                                                </span>
                                                <MdKeyboardArrowDown
                                                  size={18}
                                                  style={{
                                                    color: "#6b7280",
                                                    transform: appOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                    transition: "transform 0.15s ease",
                                                    flexShrink: 0,
                                                  }}
                                                />
                                              </button>
                                              <Collapse in={appOpen}>
                                                <div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      gap: 6,
                                                      padding: "8px 12px 12px",
                                                      maxHeight: 260,
                                                      overflowY: "auto",
                                                    }}
                                                  >
                                                    {appResources.map((resource) => {
                                                      const checked = isResourceSelected(permissionKey, resource.resource_id);
                                                      return (
                                                        <label
                                                          key={resource.resource_id}
                                                          style={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 10,
                                                            padding: "8px 10px",
                                                            borderRadius: 8,
                                                            background: checked ? "#f3f4f6" : "transparent",
                                                            cursor: "pointer",
                                                            margin: 0,
                                                          }}
                                                        >
                                                          <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => toggleResourceId(permissionKey, resource.resource_id)}
                                                            style={{ marginTop: 3 }}
                                                          />
                                                          <span style={{ flex: 1, minWidth: 0 }}>
                                                            <span style={{ display: "block", fontWeight: 600, fontSize: 13, color: "#111827" }}>
                                                              {resource.name || resource.code || `Resource #${resource.resource_id}`}
                                                            </span>
                                                            {resource.code ? (
                                                              <span className="text-muted" style={{ fontSize: 11, display: "block" }}>
                                                                {resource.code}
                                                              </span>
                                                            ) : null}
                                                            <span className="text-muted" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                                                              Created by: {resource.created_by_label || "—"}
                                                            </span>
                                                          </span>
                                                        </label>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              </Collapse>
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  </div>
                                </Collapse>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="add_app_btn btn_theme_primary"
                    onClick={savePermissions}
                    disabled={permissionsLoading || permissionsSaving}
                  >
                    <span>{permissionsSaving ? "Saving..." : "Save"}</span>
                  </button>
                  <button
                    type="button"
                    className="add_app_btn_secondary btn_theme_secondary"
                    onClick={() => setShowPermissionsModal(false)}
                  >
                    <span>Close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {openActionItem && dropdownPosition
        ? createPortal(
          <div
            ref={dropdownRef}
            style={{ position: "fixed", left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 999999, minWidth: 200 }}
          >
            <div className="dropdown-menu-subscription">
              {can("team.edit") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => openEdit(openActionItem))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Edit</span>
              </button>
              ) : null}
              {can("team.edit") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => openPermissionsModal(openActionItem))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdSecurity size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Permissions</span>
              </button>
              ) : null}
              {can("team.change_status") ? (() => {
                const pending = isStaffPending(openActionItem);
                const isSupport = Boolean(openActionItem.is_support_team_member);
                const active = isSupport
                  ? Number(openActionItem.membership_status) === 1 && openActionItem.is_member_active
                  : Number(openActionItem.status) === 1 && openActionItem.is_member_active;
                const toggleDisabled = pending;
                return (
                  <button
                    type="button"
                    disabled={toggleDisabled}
                    title={toggleDisabled ? "Pending activation — send activation email instead." : undefined}
                    onClick={() => {
                      if (toggleDisabled) return;
                      handleDropdownAction(() =>
                        changeStatus(
                          openActionItem,
                          active ? 0 : 1
                        )
                      );
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "left",
                      opacity: toggleDisabled ? 0.45 : 1,
                      cursor: toggleDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    {active ? (
                      <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    ) : (
                      <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    )}
                    <span style={{ fontSize: "0.875rem" }}>{active ? "Deactivate" : "Activate"}</span>
                  </button>
                );
              })() : null}
              {can("team.edit") ? (
              <button
                type="button"
                disabled={Boolean(openActionItem.is_member_active)}
                title={openActionItem.is_member_active ? "Member has already completed activation." : undefined}
                onClick={() => {
                  if (openActionItem.is_member_active) return;
                  handleDropdownAction(() => sendActivation(openActionItem));
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  opacity: openActionItem.is_member_active ? 0.45 : 1,
                  cursor: openActionItem.is_member_active ? "not-allowed" : "pointer",
                }}
              >
                <MdOutlineEmail size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Send activation email</span>
              </button>
              ) : null}
              {can("team.delete") ? (
              <button
                type="button"
                className="delete-class"
                onClick={() => handleDropdownAction(() => handleDelete(openActionItem))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Delete</span>
              </button>
              ) : null}
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default Staff;
